// src/pages/trading-dashboard/components/BetHistory.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const BetHistory = () => {
  const [filterBy, setFilterBy] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock bet history data
  const betHistory = [
    {
      id: 'bet_h001',
      marketTitle: 'Will OpenAI release GPT-5 in 2024?',
      outcome: 'No',
      amount: 150,
      shares: 214,
      avgPrice: 0.70,
      finalPrice: 0.85,
      realizedPnL: 32.10,
      realizedPnLPercent: 21.40,
      placedAt: '2024-01-05T11:20:00Z',
      settledAt: '2024-01-20T16:00:00Z',
      status: 'settled',
      result: 'won'
    },
    {
      id: 'bet_h002',
      marketTitle: 'Will Ethereum reach $3,000 by March 2024?',
      outcome: 'Yes',
      amount: 200,
      shares: 333,
      avgPrice: 0.60,
      finalPrice: 0.05,
      realizedPnL: -183.35,
      realizedPnLPercent: -91.68,
      placedAt: '2023-12-15T09:30:00Z',
      settledAt: '2024-01-15T12:00:00Z',
      status: 'settled',
      result: 'lost'
    },
    {
      id: 'bet_h003',
      marketTitle: 'Will COP28 agreement include fossil fuel transition?',
      outcome: 'Yes',
      amount: 75,
      shares: 150,
      avgPrice: 0.50,
      finalPrice: 1.00,
      realizedPnL: 75.00,
      realizedPnLPercent: 100.00,
      placedAt: '2023-11-28T14:45:00Z',
      settledAt: '2023-12-13T10:30:00Z',
      status: 'settled',
      result: 'won'
    },
    {
      id: 'bet_h004',
      marketTitle: 'Will Twitter rebrand to X by end of 2023?',
      outcome: 'Yes',
      amount: 100,
      shares: 250,
      avgPrice: 0.40,
      finalPrice: 1.00,
      realizedPnL: 150.00,
      realizedPnLPercent: 150.00,
      placedAt: '2023-07-20T16:00:00Z',
      settledAt: '2023-07-24T09:00:00Z',
      status: 'settled',
      result: 'won'
    },
    {
      id: 'bet_h005',
      marketTitle: 'Will FTX be acquired by another exchange in 2023?',
      outcome: 'Yes',
      amount: 120,
      shares: 171,
      avgPrice: 0.70,
      finalPrice: 0.00,
      realizedPnL: -120.00,
      realizedPnLPercent: -100.00,
      placedAt: '2023-01-10T08:15:00Z',
      settledAt: '2023-12-31T23:59:00Z',
      status: 'settled',
      result: 'lost'
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'won': return 'text-success';
      case 'lost': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'won': return 'CheckCircle';
      case 'lost': return 'XCircle';
      default: return 'Clock';
    }
  };

  const getPnLColor = (pnl) => {
    return pnl >= 0 ? 'text-success' : 'text-error';
  };

  // Filter bets
  const filteredBets = betHistory?.filter(bet => {
    if (filterBy === 'all') return true;
    if (filterBy === 'won') return bet?.result === 'won';
    if (filterBy === 'lost') return bet?.result === 'lost';
    return true;
  });

  // Sort bets
  const sortedBets = [...filteredBets]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'date':
        aValue = new Date(a.settledAt);
        bValue = new Date(b.settledAt);
        break;
      case 'amount':
        aValue = a?.amount;
        bValue = b?.amount;
        break;
      case 'pnl':
        aValue = a?.realizedPnL;
        bValue = b?.realizedPnL;
        break;
      case 'market':
        aValue = a?.marketTitle;
        bValue = b?.marketTitle;
        break;
      default:
        aValue = new Date(a.settledAt);
        bValue = new Date(b.settledAt);
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue);
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Calculate stats
  const totalAmount = betHistory?.reduce((sum, bet) => sum + bet?.amount, 0);
  const totalPnL = betHistory?.reduce((sum, bet) => sum + bet?.realizedPnL, 0);
  const winRate = betHistory?.length > 0 ? (betHistory?.filter(bet => bet?.result === 'won')?.length / betHistory?.length) * 100 : 0;

  const filterOptions = [
    { value: 'all', label: 'All Bets' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Date' },
    { value: 'amount', label: 'Amount' },
    { value: 'pnl', label: 'P&L' },
    { value: 'market', label: 'Market' }
  ];

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Total Bets</p>
              <p className="text-2xl font-data text-text-primary">{betHistory?.length}</p>
            </div>
            <Icon name="History" size={24} className="text-primary" />
          </div>
        </div>
        
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Total Amount</p>
              <p className="text-2xl font-data text-text-primary">${totalAmount}</p>
            </div>
            <Icon name="DollarSign" size={24} className="text-primary" />
          </div>
        </div>
        
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Total P&L</p>
              <p className={`text-2xl font-data ${getPnLColor(totalPnL)}`}>
                ${totalPnL?.toFixed(2)}
              </p>
            </div>
            <Icon 
              name={totalPnL >= 0 ? 'TrendingUp' : 'TrendingDown'} 
              size={24} 
              className={getPnLColor(totalPnL)} 
            />
          </div>
        </div>
        
        <div className="bg-surface-hover rounded-trading p-4 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary">Win Rate</p>
              <p className="text-2xl font-data text-text-primary">{winRate?.toFixed(1)}%</p>
            </div>
            <Icon name="Target" size={24} className="text-primary" />
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h4 className="font-body-semibold text-text-primary">Betting History</h4>
        
        <div className="flex items-center space-x-2">
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e?.target?.value)}
            className="px-3 py-2 text-sm border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
          >
            {filterOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 text-sm border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
          >
            {sortOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                Sort by {option?.label}
              </option>
            ))}
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-2 border border-border rounded-trading hover:bg-surface-hover transition-colors duration-trading-fast"
          >
            <Icon 
              name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
              size={16} 
              className="text-text-secondary" 
            />
          </button>
        </div>
      </div>
      {/* Bet History Table */}
      <div className="space-y-3">
        {sortedBets?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="History" size={48} className="text-text-secondary mx-auto mb-3" />
            <p className="text-text-secondary">
              {filterBy === 'all' ? 'No betting history' : `No ${filterBy} bets found`}
            </p>
            <p className="text-sm text-text-secondary mt-1">
              Your settled prediction market bets will appear here
            </p>
          </div>
        ) : (
          sortedBets?.map((bet) => (
            <div
              key={bet?.id}
              className="bg-surface-hover border border-border rounded-trading p-4 hover:border-border-hover transition-all duration-trading-fast"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h5 className="font-body-semibold text-text-primary leading-tight mb-1 line-clamp-2">
                    {bet?.marketTitle}
                  </h5>
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <span className="flex items-center">
                      <Icon name="Calendar" size={14} className="mr-1" />
                      Settled {formatDate(bet?.settledAt)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <Icon 
                    name={getResultIcon(bet?.result)} 
                    size={20} 
                    className={getResultColor(bet?.result)} 
                  />
                  <span className={`text-sm font-body-medium ${getResultColor(bet?.result)}`}>
                    {bet?.result?.charAt(0)?.toUpperCase() + bet?.result?.slice(1)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 text-sm">
                <div>
                  <p className="text-text-secondary">Outcome</p>
                  <p className="font-body-medium text-text-primary">{bet?.outcome}</p>
                </div>
                
                <div>
                  <p className="text-text-secondary">Amount</p>
                  <p className="font-data text-text-primary">${bet?.amount}</p>
                </div>
                
                <div>
                  <p className="text-text-secondary">Shares</p>
                  <p className="font-data text-text-primary">{bet?.shares}</p>
                </div>
                
                <div>
                  <p className="text-text-secondary">Avg Price</p>
                  <p className="font-data text-text-primary">${bet?.avgPrice?.toFixed(3)}</p>
                </div>
                
                <div>
                  <p className="text-text-secondary">Final Price</p>
                  <p className="font-data text-text-primary">${bet?.finalPrice?.toFixed(3)}</p>
                </div>
                
                <div>
                  <p className="text-text-secondary">Realized P&L</p>
                  <div className="flex items-center space-x-1">
                    <p className={`font-data ${getPnLColor(bet?.realizedPnL)}`}>
                      ${bet?.realizedPnL?.toFixed(2)}
                    </p>
                    <span className={`text-xs ${getPnLColor(bet?.realizedPnL)}`}>
                      ({bet?.realizedPnLPercent > 0 ? '+' : ''}{bet?.realizedPnLPercent?.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                
                <div>
                  <p className="text-text-secondary">Duration</p>
                  <p className="font-data text-text-primary">
                    {Math.ceil((new Date(bet.settledAt) - new Date(bet.placedAt)) / (1000 * 60 * 60 * 24))}d
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Export Button */}
      {sortedBets?.length > 0 && (
        <div className="flex justify-center mt-6">
          <button className="flex items-center space-x-2 px-4 py-2 bg-secondary-100 text-text-primary rounded-trading hover:bg-secondary-200 transition-colors duration-trading-fast">
            <Icon name="Download" size={16} />
            <span className="font-body-medium text-sm">Export History</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default BetHistory;